import type { AttemptFeedback } from '@engineer-os/engine';

export type MeasuredClient = 'claude' | 'codex';
export type CodexModel = 'gpt-6.1-sol' | 'gpt-6-luna';
export type MeasuredModel = { client: 'codex'; id: CodexModel } | { client: 'claude'; id: string };
const storageKey = (userId: string) => `engineeros.measuredClient.${userId}`;

/** A preference belongs to this guest/account and this tab, never another owner. */
export function storedMeasuredClient(userId: string): MeasuredClient {
  try { return sessionStorage.getItem(storageKey(userId)) === 'codex' ? 'codex' : 'claude'; }
  catch { return 'claude'; }
}

export function rememberMeasuredClient(userId: string, client: MeasuredClient): void {
  try { sessionStorage.setItem(storageKey(userId), client); } catch { /* The current mount still works. */ }
}

/** Replay advice follows recorded usage, rather than the visitor's last preference. */
export function measuredClientFromUsage(usage: AttemptFeedback['usage'] | undefined): MeasuredClient | null {
  const models = usage?.models;
  if (!models?.length) return null;
  if (models.every(({ model }) => model.startsWith('openai/'))) return 'codex';
  if (models.every(({ model }) => model.startsWith('anthropic/'))) return 'claude';
  return null;
}

/** Process comparisons keep one identifiable actual model, including a Luna baseline. */
export function measuredModelFromUsage(usage: AttemptFeedback['usage'] | undefined, baselineModels?: string[] | null): MeasuredModel | null {
  const models = [...new Set(usage?.models.map(({ model }) => model) ?? [])];
  // When the server supplies whole-baseline membership, every retry must
  // agree with the passing attempt. Legacy responses retain their old path.
  if (baselineModels !== undefined && (!baselineModels || baselineModels.length !== 1 || models.length !== 1 || baselineModels[0] !== models[0])) return null;
  if (models.length !== 1) return null;
  const model = models[0];
  if (!model) return null;
  if (model === 'openai/gpt-6.1-sol' || model === 'openai/gpt-6-luna') return { client: 'codex', id: model.slice(7) as CodexModel };
  // Price-book Claude names may use dotted versions that are not native
  // Claude Code IDs. Without a verified native mapping, leave setup explicit.
  return null;
}

export function replayMeasuredModel(baseline: MeasuredModel | null, smaller: boolean): MeasuredModel | null {
  if (!baseline || !smaller) return baseline;
  if (baseline.client === 'codex') return baseline.id === 'gpt-6.1-sol' ? { client: 'codex', id: 'gpt-6-luna' } : null;
  return /(?:sonnet|opus)/i.test(baseline.id) ? { client: 'claude', id: 'haiku' } : null;
}
